/**
 * Utilities for detecting and clearing stale MSAL/Azure B2C session data.
 *
 * After the hard cutoff from MSAL to Supabase-only auth, users with lingering
 * MSAL session storage will get a friendly modal explaining they need to re-login.
 * These utilities detect such sessions and clear them when the user takes action.
 *
 * Detection works by checking localStorage/sessionStorage keys matching the MSAL
 * cache key patterns (set with cacheLocation: "localStorage").
 */

/**
 * Check if there's a stale MSAL session in browser storage.
 * Returns true if localStorage contains MSAL account or token cache entries.
 */
export function hasStaleMsalSession(): boolean {
  try {
    // Check for MSAL account keys (primary indicator)
    const accountKeys = JSON.parse(
      localStorage.getItem("msal.account.keys") ?? "[]",
    );
    if (Array.isArray(accountKeys) && accountKeys.length > 0) {
      return true;
    }
  } catch {
    // Ignore malformed cache entries
  }

  // Check for MSAL token cache keys (secondary indicator)
  const hasTokenKeys = Object.keys(localStorage).some((k) =>
    k.startsWith("msal.token.keys."),
  );
  if (hasTokenKeys) {
    return true;
  }

  return false;
}

/**
 * Clear all MSAL/Azure B2C session data from browser storage.
 * Removes entries from localStorage, sessionStorage, and cookies.
 *
 * This should only be called when the user explicitly acts (e.g., clicks "Go to Login"
 * in the migration modal), not silently on app init.
 */
export function clearMsalSession(): void {
  // Clear localStorage
  const lsKeysToDelete = Object.keys(localStorage).filter((k) =>
    k.startsWith("msal."),
  );
  lsKeysToDelete.forEach((k) => localStorage.removeItem(k));

  // Clear sessionStorage
  const ssKeysToDelete = Object.keys(sessionStorage).filter((k) =>
    k.startsWith("msal."),
  );
  ssKeysToDelete.forEach((k) => sessionStorage.removeItem(k));

  // Clear MSAL cookies
  const allCookies = document.cookie.split(";");
  allCookies.forEach((cookie) => {
    const name = cookie.split("=")[0].trim();
    if (name.startsWith("msal.")) {
      // Set expiration in the past to delete
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    }
  });

  console.log("[MSAL Migration] Cleared all MSAL session data from storage");
}
